import { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
    CircularProgress, Alert,
} from '@mui/material';

const PP = "'Poppins', sans-serif";

// Commission / Incentive / Extra slip approval review (user 2026-08-24).
//
// Approving used to be a bare window.confirm — "Approve this Commission slip?" — which
// showed neither the slip, the agent, the amount, nor the fee. An approver was signing off
// money they could not see. This dialog lists EVERY slip in the batch with its own
// breakdown and a batch total, so approval is a decision rather than a reflex.
//
// Each row is hydrated individually: the flat slip list carries only totalPayout, while
// gross commission and processing fee live on the type-specific payload. A hydrate that
// fails degrades to the list figures rather than blocking the approval.
const rm = (n) => `RM ${Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (part, whole) => (!whole ? '—' : `${(Number(part || 0) / Number(whole) * 100).toFixed(1)}%`);

export default function ApproveSlipsDialog({ open, onClose, slips, hydrate, onConfirm }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const pending = (slips || []).filter(s => (s.status || 'Pending') === 'Pending');
    const skipped = (slips || []).length - pending.length;

    const load = useCallback(async () => {
        if (!open || !pending.length) { setRows([]); return; }
        setLoading(true); setError('');
        try {
            const out = [];
            for (const s of pending) {
                let full = s;
                try {
                    // eslint-disable-next-line no-await-in-loop
                    if (hydrate) full = { ...s, ...(await hydrate(s)) };
                } catch {
                    // Keep the list figures — a failed hydrate must not hide the slip.
                }
                const gross = full.grossCommission ?? full.totalPayout ?? full.amount ?? 0;
                const fee = full.processingFee ?? 0;
                const net = full.totalPayout ?? full.amount ?? (gross - fee);
                out.push({
                    key: `${s.type}-${s.slipId}`,
                    slipId: s.slipId, type: s.type,
                    agentName: full.agentName || s.agentName || '—',
                    period: full.period || s.period || '—',
                    dateIssued: full.dateIssued || s.dateIssued || null,
                    gross, fee, net,
                });
            }
            setRows(out);
        } catch (e) {
            setError(e?.response?.data?.message || e.message || 'Could not load slip details.');
        } finally { setLoading(false); }
    }, [open, slips]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const totGross = rows.reduce((s, r) => s + Number(r.gross || 0), 0);
    const totFee = rows.reduce((s, r) => s + Number(r.fee || 0), 0);
    const totNet = rows.reduce((s, r) => s + Number(r.net || 0), 0);

    const confirm = async () => {
        setSaving(true);
        try { await onConfirm(pending); }
        finally { setSaving(false); }
    };

    const th = { textAlign: 'left', padding: '7px 10px', fontSize: '0.68rem', letterSpacing: '.06em',
                 textTransform: 'uppercase', color: '#64748B', fontWeight: 600, whiteSpace: 'nowrap',
                 borderBottom: '1px solid #CBD5E1', background: '#F8FAFC' };
    const td = { padding: '8px 10px', fontSize: '0.8rem', borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap' };
    const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ fontFamily: PP, fontWeight: 600, fontSize: '1.05rem', pb: 0.5 }}>
                Approve {pending.length} slip{pending.length === 1 ? '' : 's'}
            </DialogTitle>

            <DialogContent dividers>
                <Typography sx={{ fontFamily: PP, fontSize: '0.8rem', color: '#64748B', mb: 1.5 }}>
                    Check each slip before approving. Approval closes the round — later payments
                    for the same period must go into the next open round.
                </Typography>

                {skipped > 0 && (
                    <Alert severity="info" sx={{ mb: 1.5, fontFamily: PP, fontSize: '0.8rem' }}>
                        {skipped} selected row{skipped === 1 ? '' : 's'} already approved or voided — skipped.
                    </Alert>
                )}
                {error && <Alert severity="error" sx={{ mb: 1.5, fontFamily: PP, fontSize: '0.8rem' }}>{error}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
                ) : !pending.length ? (
                    <Typography sx={{ fontFamily: PP, fontSize: '0.85rem', py: 2 }}>No pending slips selected.</Typography>
                ) : (
                    <Box sx={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: 1 }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', fontFamily: PP, minWidth: 760 }}>
                            <thead>
                                <tr>
                                    <th style={th}>Slip</th>
                                    <th style={th}>Type</th>
                                    <th style={th}>Agent</th>
                                    <th style={th}>Period</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Gross</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Fee</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Fee %</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Net payout</th>
                                    <th style={{ ...th, textAlign: 'right' }}>% of batch</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.key}>
                                        <td style={td}>#{r.slipId}</td>
                                        <td style={td}>{r.type}</td>
                                        <td style={{ ...td, whiteSpace: 'normal' }}>{r.agentName}</td>
                                        <td style={td}>{r.period}</td>
                                        <td style={tdNum}>{rm(r.gross)}</td>
                                        <td style={tdNum}>{r.fee ? rm(r.fee) : '—'}</td>
                                        <td style={{ ...tdNum, color: '#64748B' }}>{r.fee ? pct(r.fee, r.gross) : '—'}</td>
                                        <td style={{ ...tdNum, fontWeight: 600 }}>{rm(r.net)}</td>
                                        <td style={{ ...tdNum, color: '#64748B' }}>{pct(r.net, totNet)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td style={{ ...td, fontWeight: 600, borderBottom: 'none' }} colSpan={4}>
                                        {rows.length} slip{rows.length === 1 ? '' : 's'}
                                    </td>
                                    <td style={{ ...tdNum, fontWeight: 600, borderBottom: 'none' }}>{rm(totGross)}</td>
                                    <td style={{ ...tdNum, fontWeight: 600, borderBottom: 'none' }}>{totFee ? rm(totFee) : '—'}</td>
                                    <td style={{ ...tdNum, borderBottom: 'none' }}>—</td>
                                    <td style={{ ...tdNum, fontWeight: 700, borderBottom: 'none' }}>{rm(totNet)}</td>
                                    <td style={{ ...tdNum, borderBottom: 'none' }}>—</td>
                                </tr>
                            </tfoot>
                        </table>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={saving} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={confirm}
                    disabled={saving || loading || !pending.length}
                    sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600 }}
                >
                    {saving ? 'Approving…' : `Approve ${pending.length} slip${pending.length === 1 ? '' : 's'} — ${rm(totNet)}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
